<script setup lang="ts">
import { regex2FA } from "~/constants/regex.constants";

const emit = defineEmits(["sendCode", "resend", "update:modelValue"]);
const { t } = useI18n();

onMounted(() => {
  timerId.value = setInterval(() => {
    remainingTime.value -= 1;
    if (remainingTime.value === 0) {
      clearInterval(timerId.value);
      isResendClicked.value = false;
      remainingTime.value = 60;
    }
  }, 1000);
  firstInputEl.value[0].focus();
});

onUnmounted(() => {
  clearInterval(timerId.value);
});

const fields = 6;
const input2FAValues = ref<string[]>(Array(6).fill(""));
const firstInputEl = ref();
const errorMessage = ref<string>();
const isResendClicked = ref<boolean>(true);
const remainingTime = ref<number>(30);
const timerId = ref();
const isLoading = ref(false);

watch(
  () => input2FAValues,
  (newVal) => {
    if (
      newVal.value[0] != "" &&
      newVal.value.length === fields &&
      !newVal.value.includes("")
    ) {
      emit("update:modelValue", Number(newVal.value.join("")));
    } else {
      emit("update:modelValue", null);
    }
  },
  { deep: true }
);

const resend2FA = async () => {
  isResendClicked.value = true;
  timerId.value = setInterval(() => {
    remainingTime.value -= 1;
    if (remainingTime.value === 0) {
      clearInterval(timerId.value);
      isResendClicked.value = false;
      remainingTime.value = 60;
    }
  }, 1000);
  emit("resend");
};

const handle2FAInput = (e: Event) => {
  const inputEvent = e as InputEvent;
  const target = e.target as HTMLInputElement;
  if (!target) return;

  // Handle Samsung and Google keyboard paste pattern
  // They trigger an input event with the full pasted value
  if (
    inputEvent.inputType === "insertText" &&
    target.value &&
    target.value.length > 1
  ) {
    const pastedValue = target.value.replace(/\s/g, "").substring(0, 6);
    if (pastedValue.length >= 6 && regex2FA.test(pastedValue)) {
      e.preventDefault();
      // Reset current field value to just the first digit
      target.value = pastedValue[0];
      input2FAValues.value[Number(target.id) - 1] = pastedValue[0];

      // Fill the rest of the fields
      for (let i = 1; i < fields && i < pastedValue.length; i++) {
        input2FAValues.value[i] = pastedValue[i];
        if (firstInputEl.value && firstInputEl.value[i]) {
          firstInputEl.value[i].value = pastedValue[i];
        }
      }

      // Focus the last field
      setTimeout(() => {
        const inputs = firstInputEl.value;
        if (inputs && inputs.length >= 6) {
          inputs[5].focus();
        }
      }, 0);

      validate2fa(pastedValue);
      return;
    }
  }

  if (inputEvent.data && target.nextElementSibling) {
    (target.nextElementSibling as HTMLElement).focus();
  }

  if (
    target.id === "6" &&
    input2FAValues.value.every((value) => value !== "")
  ) {
    validate2fa(input2FAValues.value.join(""));
  }
};

const handleKeyDown = (e: KeyboardEvent) => {
  const target = e.target as HTMLInputElement;
  if (!target) return;

  // Detect mobile keyboard paste via keydown event (e.g. "123456")
  // Mobile keyboard paste appears as a single keydown with a multi-character key
  if (e.key && e.key.length > 1) {
    const potentialOTP = e.key.replace(/\s/g, "");
    if (potentialOTP.length === 6 && /^[a-zA-Z0-9]{6}$/.test(potentialOTP)) {
      e.preventDefault();
      for (let i = 0; i < 6; i++) {
        input2FAValues.value[i] = potentialOTP[i];
      }
      setTimeout(() => {
        const inputs = firstInputEl.value;
        if (inputs && inputs.length >= 6) {
          inputs[5].focus();
        }
      }, 0);

      validate2fa(potentialOTP);
      return;
    }
  }

  if (e.key === "Backspace") {
    if (target.value) {
      input2FAValues.value[Number(target.id) - 1] = "";
      target.value = "";
      emit("update:modelValue", null);
    } else if (target.previousElementSibling) {
      (target.previousElementSibling as HTMLElement).focus();
    }
  }
};

const handlePaste = (e: ClipboardEvent) => {
  const clipboardData = e.clipboardData;
  if (!clipboardData) return;
  isLoading.value = true;
  const pasteData = clipboardData.getData("text").substring(0, 6);
  input2FAValues.value[0] = pasteData[0];
  let nextEl = firstInputEl.value[0].nextElementSibling;
  for (let i = 1; i < fields; i++) {
    if (nextEl) {
      input2FAValues.value[i] = pasteData[i];
      nextEl = nextEl.nextElementSibling;
    }
  }
  validate2fa(pasteData);
};

const validate2fa = (code: string) => {
  errorMessage.value = "";
  isLoading.value = true;
  if (code && !regex2FA.test(code)) {
    errorMessage.value = t("errorMessages.code2FAInvalid");
    isLoading.value = false;
    return false;
  }
  emit("sendCode", code.toUpperCase());
};
</script>
<template>
  <div>
    <p class="mt-4 mb-2 text-sm font-bold text-neutral-300">
      {{ $t("main.input2FA.enterCode") }}
    </p>
    <form
      id="input2faForm"
      @submit.prevent="validate2fa(input2FAValues.join(''))">
      <div
        class="2FA w-full flex"
        @input="handle2FAInput"
        @keydown="handleKeyDown">
        <template v-for="field in fields" :key="field">
          <input
            :id="String(field)"
            :disabled="isLoading"
            ref="firstInputEl"
            v-model="input2FAValues[field - 1]"
            type="text"
            autocomplete="one-time-code"
            class="bg-white w-12 h-10 uppercase text-center text-lg font-semibold border border-primary-400 first:rounded-l last:rounded-r outline-primary-400 focus:ring-1 focus:ring-inset focus:ring-primary-400 focus:border focus:border-primary-400"
            @paste="field === 1 && handlePaste($event)" />
        </template>
      </div>
      <p class="text-sm h-12 flex items-center text-error-300">
        {{ errorMessage }}
      </p>
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-4 -mt-2">
          <BaseButtonSubmit
            :loading="isLoading"
            :disabled="isLoading || input2FAValues.join('').length < 6"
            text="Verify"
            data-test="button-verify-2fa"
            size="long" />
        </div>
      </div>
    </form>
    <div class="flex gap-2 mt-4">
      <button
        v-if="!isResendClicked"
        class="flex gap-1 items-center text-sm font-bold text-primary-500"
        data-test="button-resend-2fa"
        :disabled="isResendClicked"
        @click="resend2FA">
        {{ $t("main.input2FA.resendCode") }}
        <NuxtIcon
          name="mdi:refresh"
          class="text-primary-500"
          :alt="$t('main.imageAltTexts.2FAResendIcon')" />
      </button>
      <p v-else class="text-neutral-300 text-sm">
        {{ $t("main.input2FA.resendCodeIn") }}
        {{ `00:${remainingTime < 10 ? `0${remainingTime}` : remainingTime}` }}
      </p>
    </div>
  </div>
</template>
